import { type CreateUserResponse, CreateUserRequestSchema, type Role } from "@pokifolio/contracts";
import { type SubmitEvent, useState } from "react";

import { useApi } from "../../app/api-context";
import { ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card, CardDescription, CardTitle } from "../../components/ui/card";
import { Field } from "../../components/ui/field";
import { Input, Select } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { type FieldErrors, validate } from "../../lib/forms";
import { useUserMutation } from "./users-queries";

export function CreateUserCard({ onCreated }: { onCreated: (created: CreateUserResponse) => void }) {
  const api = useApi();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("employee");
  const [errors, setErrors] = useState<FieldErrors>({});
  const create = useUserMutation(api.createUser);

  function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(CreateUserRequestSchema, { name, email, role });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    create.mutate(result.data, {
      onSuccess: (created) => {
        setName("");
        setEmail("");
        setRole("employee");
        onCreated(created);
      },
    });
  }

  return (
    <Card>
      <CardTitle>Add a person</CardTitle>
      <CardDescription>
        They receive a temporary password and must choose their own at first sign-in.
      </CardDescription>
      <form
        onSubmit={onSubmit}
        noValidate
        className="mt-5 grid gap-4 md:grid-cols-[1fr_1fr_10rem_auto] md:items-start"
      >
        <Field
          id="new-user-name"
          label="Full name"
          error={errors.name ?? serverFieldError(create.error, "name")}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <Field
          id="new-user-email"
          label="Email"
          error={errors.email ?? serverFieldError(create.error, "email")}
        >
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
        </Field>
        <Field id="new-user-role" label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value === "admin" ? "admin" : "employee")}>
            <option value="employee">Employee</option>
            <option value="admin">Admin</option>
          </Select>
        </Field>
        <Button type="submit" className="md:mt-6" disabled={create.isPending}>
          {create.isPending ? "Creating…" : "Create account"}
        </Button>
      </form>
      {create.isError && serverFieldError(create.error, "email") === undefined && (
        <ErrorAlert className="mt-4" error={create.error} />
      )}
    </Card>
  );
}
